<template>
  <div style="width: 100%; padding: 10px">
    <div class="d-flex flex-column flex-sm-row justify-space-between align-start align-sm-center mb-5">
      <h1 class="mb-3 mb-sm-0">{{ $t("fitness_workouts.header") }}</h1>
      <v-btn color="primary" @click="openAddDialog" :loading="saving" class="align-self-stretch align-self-sm-auto">
        <v-icon left>fas fa-play</v-icon>
        {{ $t("fitness_workouts.add_workout") }}
      </v-btn>
    </div>
    <!-- KPI Timeframe Selector -->
    <div class="d-flex justify-center mb-4 mt-n2">
       <v-btn-toggle v-model="kpiTimeframe" mandatory dense active-class="primary white--text">
          <v-btn value="day">{{ $t('fitness_workouts.timeframe_day') || 'Day' }}</v-btn>
          <v-btn value="week">{{ $t('fitness_workouts.timeframe_week') || 'Week' }}</v-btn>
          <v-btn value="month">{{ $t('fitness_workouts.timeframe_month') || 'Month' }}</v-btn>
       </v-btn-toggle>
    </div>

    <!-- Main KPIs -->
    <v-row class="mb-0 mx-n1 mx-sm-0">
      <v-col v-for="(kpi, index) in orderedKPIs.slice(0, 3)" :key="'main-' + index" cols="4" sm="4" class="px-1 px-sm-3 mb-2">
        <v-card outlined class="pa-1 pa-sm-3 text-center" style="height: 100%; display: flex; flex-direction: column; justify-content: center;">
          <div class="text-caption text-uppercase grey--text" style="font-size: 0.65rem !important; line-height: 1.1;">{{ kpi.title }}</div>
          <div class="text-h5 text-sm-h4 font-weight-bold my-1 my-sm-2">
            {{ kpi.value }} <span v-if="kpi.unit" class="text-body-2 text-sm-body-1">{{ kpi.unit }}</span>
          </div>
          <div :class="kpi.percent >= 0 ? 'success--text' : 'error--text'" class="text-caption text-sm-body-2 font-weight-bold" style="line-height: 1.1;">
            <v-icon x-small :color="kpi.percent >= 0 ? 'success' : 'error'">{{ kpi.percent >= 0 ? 'fas fa-arrow-up' : 'fas fa-arrow-down' }}</v-icon>
            {{ Math.abs(kpi.percent) }}% <span class="hidden-xs-only">{{ kpi.vsText }}</span>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Expandable KPIs -->
    <v-expand-transition>
      <div v-show="showMoreKPIs">
        <v-row class="mb-0 mx-n1 mx-sm-0">
          <v-col v-for="(kpi, index) in orderedKPIs.slice(3)" :key="'more-' + index" cols="4" sm="4" class="px-1 px-sm-3 mb-2">
            <v-card outlined class="pa-1 pa-sm-3 text-center" style="height: 100%; display: flex; flex-direction: column; justify-content: center;">
              <div class="text-caption text-uppercase grey--text" style="font-size: 0.65rem !important; line-height: 1.1;">{{ kpi.title }}</div>
              <div class="text-h5 text-sm-h4 font-weight-bold my-1 my-sm-2">
                {{ kpi.value }} <span v-if="kpi.unit" class="text-body-2 text-sm-body-1">{{ kpi.unit }}</span>
              </div>
              <div :class="kpi.percent >= 0 ? 'success--text' : 'error--text'" class="text-caption text-sm-body-2 font-weight-bold" style="line-height: 1.1;">
                <v-icon x-small :color="kpi.percent >= 0 ? 'success' : 'error'">{{ kpi.percent >= 0 ? 'fas fa-arrow-up' : 'fas fa-arrow-down' }}</v-icon>
                {{ Math.abs(kpi.percent) }}% <span class="hidden-xs-only">{{ kpi.vsText }}</span>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </v-expand-transition>
    
    <div class="text-center mb-4 mt-n2">
      <v-btn text small color="grey" @click="showMoreKPIs = !showMoreKPIs">
        <v-icon left small>{{ showMoreKPIs ? 'fas fa-chevron-up' : 'fas fa-chevron-down' }}</v-icon>
        {{ showMoreKPIs ? ($t('general.close') || 'Close') : ($t('fitness_workouts.more_insights') || 'More Insights') }}
      </v-btn>
    </div>

    <!-- Filter & Search -->
    <v-card flat outlined class="mb-4 pa-3">
      <v-row align="center">
        <v-col cols="12" md="4">
          <v-text-field
            v-model="search"
            append-icon="fas fa-search"
            :label="$t('fitness_workouts.search_workouts') || 'Search Workouts'"
            hide-details
            outlined
            dense
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4">
          <v-autocomplete
            v-model="filterType"
            :items="filterOptions"
            :label="$t('fitness_workouts.activities')"
            hide-details
            outlined
            dense
          ></v-autocomplete>
        </v-col>
      </v-row>
    </v-card>

    <div v-if="filteredWorkouts.length === 0 && !loading" class="text-center mt-10 mb-10 text--secondary" style="font-size: 1.1em; font-style: italic;">
      <v-icon size="48" color="grey lighten-1" class="mb-3">fad fa-folder-open</v-icon><br>
      {{ $t("fitness_workouts.no_activities") }}
    </div>

    <v-row v-else class="mt-4 mb-4 mx-n2 mx-sm-0 align-stretch">
      <v-col cols="12" sm="6" md="4" lg="3" v-for="item in filteredWorkouts" :key="item.id" class="px-2 px-sm-3 d-flex">
        <v-card 
          outlined 
          class="workout-card flex-grow-1 d-flex flex-column" 
          elevation="2"
          @click="editItem(item)"
          style="cursor: pointer; border-radius: 12px; transition: transform 0.2s, box-shadow 0.2s; min-height: 160px; width: 100%;"
          v-ripple
        >
          <div class="pa-4 d-flex flex-column flex-grow-1">
            <div class="d-flex justify-space-between align-start mb-2">
              <div class="text-h6 font-weight-bold text-truncate pr-2" style="line-height: 1.2;">
                {{ item.name }}
              </div>
              <v-chip v-if="item.is_draft" small color="orange" text-color="white" class="flex-shrink-0 font-weight-bold">
                {{ $t("fitness_workouts.draft") }}
              </v-chip>
            </div>
            
            <div class="d-flex align-center text-body-2 text--secondary mb-4" style="flex-wrap: wrap;">
              <div class="d-flex align-center mr-3">
                <v-icon x-small class="mr-1">fas fa-calendar-day</v-icon>
                <span>{{ formatDate(item.date) }}</span>
              </div>
              
              <div v-if="item.duration" class="d-flex align-center">
                <v-icon x-small class="mr-1">fas fa-stopwatch</v-icon>
                <span>{{ item.duration }} min</span>
              </div>
            </div>

            <v-divider class="mb-3"></v-divider>

            <div class="d-flex flex-wrap flex-grow-1 mb-2" style="gap: 6px;">
              <template v-if="item.activities && item.activities.length > 0">
                <v-chip v-for="(act, idx) in item.activities.slice(0, 3)" :key="idx" small dark :color="getActivityColor(act)" style="font-weight: bold;">
                  <v-icon x-small left>{{ act.type === 'cardio' ? 'fas fa-running' : 'fas fa-dumbbell' }}</v-icon>
                  {{ getActivityName(act.name) }}
                </v-chip>
                <v-chip v-if="item.activities.length > 3" small outlined color="grey" style="font-weight: bold; border-width: 2px;">
                  +{{ item.activities.length - 3 }}
                </v-chip>
              </template>
              <div v-else class="text--disabled text-caption" style="font-style: italic;">
                {{ $t("fitness_workouts.no_activities") }}
              </div>
            </div>
            
            <div class="d-flex justify-end mt-auto pt-2">
              <v-icon small color="grey">fas fa-chevron-right</v-icon>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="dialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card :style="{ backgroundColor: $vuetify.theme.dark ? '#121212' : '#f4f5f7' }">
        <v-toolbar dark color="primary" style="position: sticky; top: 0; z-index: 10;">
          <v-btn icon dark @click="close">
            <v-icon>fas fa-times</v-icon>
          </v-btn>
          <v-toolbar-title class="hidden-xs-only">
            {{ formTitle }}
            <span v-if="isSavingDraft" style="font-size: 0.7em; margin-left: 10px; opacity: 0.8;">
              <v-icon small class="fa-spin mr-1">fas fa-circle-notch</v-icon> {{ $t("fitness_workouts.saving") }}
            </span>
            <span v-else-if="dialog && editedItem.is_draft" style="font-size: 0.7em; margin-left: 10px; opacity: 0.8;">
              <v-icon small class="mr-1">fas fa-check</v-icon> {{ $t("fitness_workouts.saved") }}
            </span>
          </v-toolbar-title>
          
          <div class="hidden-sm-and-up ml-1">
            <span v-if="isSavingDraft" style="opacity: 0.8; display: flex; align-items: center;">
              <v-icon small class="fa-spin">fas fa-circle-notch</v-icon>
            </span>
            <span v-else-if="dialog && editedItem.is_draft" style="opacity: 0.8; display: flex; align-items: center;">
              <v-icon small>fas fa-check</v-icon>
            </span>
          </div>

          <v-spacer></v-spacer>
          
          <div v-if="editedItem.is_draft" class="mr-2 mr-sm-4 d-flex align-center" style="font-size: 1.1em; font-weight: bold; background: rgba(0,0,0,0.2); padding: 5px 10px; border-radius: 20px;">
            <v-icon small class="mr-1 mr-sm-2 fa-spin">fas fa-stopwatch</v-icon> {{ activeTimer }}
          </div>

          <v-toolbar-items>
            <v-btn v-if="editedItem.is_draft" color="success" elevation="0" @click="finishWorkout" :loading="saving" class="white--text font-weight-bold px-2 px-sm-4">
              <v-icon :class="{'mr-0 mr-sm-2': true}" small>fas fa-flag-checkered</v-icon>
              <span class="hidden-xs-only">{{ $t("fitness_workouts.finish_workout") }}</span>
            </v-btn>
            <v-btn v-else dark text @click="saveComplete" :loading="saving" style="font-weight: bold;" class="px-2 px-sm-4">
              <v-icon :class="{'mr-0 mr-sm-2': true}" small>fas fa-save</v-icon>
              <span class="hidden-xs-only">{{ $t("general.save") }}</span>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <v-card-text style="padding-top: 30px;">
          <v-container>
            <v-row>
              <v-col cols="12" :sm="editedItem.is_draft ? 8 : 12" :md="editedItem.is_draft ? 8 : 12">
                <v-text-field 
                  v-model="editedItem.name" 
                  :placeholder="suggestedWorkoutName" 
                  :label="$t('fitness_workouts.workout_name')" 
                  prepend-inner-icon="fas fa-heading"
                  outlined 
                  dense
                  hide-details="auto"
                  :background-color="$vuetify.theme.dark ? '#1e1e1e' : 'white'"
                ></v-text-field>
              </v-col>
              <v-col cols="12" :sm="editedItem.is_draft ? 4 : 4" :md="editedItem.is_draft ? 4 : 4">
                <v-menu
                  v-model="menu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                  :disabled="editedItem.is_draft"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDate(editedItem.date)"
                      :label="$t('fitness_workouts.date')"
                      prepend-inner-icon="fas fa-calendar-day"
                      readonly
                      outlined
                      dense
                      hide-details="auto"
                      :background-color="$vuetify.theme.dark ? '#1e1e1e' : 'white'"
                      v-bind="attrs"
                      v-on="editedItem.is_draft ? {} : on"
                      :disabled="editedItem.is_draft"
                      :hint="editedItem.is_draft ? $t('fitness_workouts.locked_during_session') : ''"
                      persistent-hint
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="editedItem.date"
                    @input="menu = false"
                    :first-day-of-week="1"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              
              <v-col v-if="!editedItem.is_draft" cols="12" sm="4" md="4">
                 <v-text-field
                    type="time"
                    v-model="startTimeString"
                    :label="$t('general.time')"
                    prepend-inner-icon="fas fa-clock"
                    outlined
                    dense
                    hide-details="auto"
                    :background-color="$vuetify.theme.dark ? '#1e1e1e' : 'white'"
                 ></v-text-field>
              </v-col>

              <v-col v-if="!editedItem.is_draft" cols="12" sm="4" md="4">
                 <v-text-field
                    v-model="editedItem.duration"
                    :label="$t('fitness_workouts.duration_min')"
                    :placeholder="String(suggestedDuration)"
                    type="number"
                    prepend-inner-icon="fas fa-stopwatch"
                    outlined
                    dense
                    hide-details="auto"
                    :background-color="$vuetify.theme.dark ? '#1e1e1e' : 'white'"
                 >
                    <template v-slot:append>
                       <v-btn icon small @click="editedItem.duration = suggestedDuration" tabindex="-1">
                          <v-icon small color="primary">fas fa-magic</v-icon>
                       </v-btn>
                    </template>
                 </v-text-field>
              </v-col>
            </v-row>

            <v-divider class="my-6"></v-divider>
            
            <div class="d-flex flex-column flex-sm-row justify-space-between align-start align-sm-center mb-5">
              <h2 class="mb-3 mb-sm-0">{{ $t("fitness_workouts.activities") }}</h2>
              <v-btn color="secondary" @click="addActivity" elevation="2" class="px-3 align-self-stretch align-self-sm-auto">
                <v-icon left small>fas fa-plus</v-icon>
                <span>{{ $t("fitness_workouts.add_activity") }}</span>
              </v-btn>
            </div>

            <v-card v-for="(activity, index) in editedItem.activities" :key="index" class="mb-6 pa-3 pa-sm-5" outlined elevation="3" :style="{ borderRadius: '10px', borderWidth: '2px', borderColor: getActivityColor(activity) }">
              <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 15px;">
                <div style="display: flex; flex-direction: column; flex-grow: 1; margin-right: 10px;">
                  <v-combobox
                    v-model="activity.name"
                    @change="autoAssignType(activity)"
                    @input="autoAssignType(activity)"
                    :items="availableActivities"
                    item-text="text"
                    item-value="value"
                    :return-object="false"
                    :label="$t('fitness_workouts.exercise_name')"
                    outlined
                    dense
                    clearable
                    hide-details
                    style="font-weight: bold; margin-bottom: 10px;"
                  >
                    <template v-slot:selection="{ item }">
                      {{ item && typeof item === 'object' ? item.text : getActivityName(item) }}
                    </template>
                    <template v-slot:item="{ item }">
                      <v-icon small left color="primary">{{ isCardio(item && item.value ? item.value : item) ? 'fas fa-running' : 'fas fa-dumbbell' }}</v-icon>
                      {{ item && typeof item === 'object' ? item.text : item }}
                    </template>
                    <template v-slot:append-outer>
                      <v-btn v-if="getExRxLink(activity.name)" icon small color="info" :href="getExRxLink(activity.name)" target="_blank" title="View on ExRx.net" style="margin-top: -4px;">
                        <v-icon small>fas fa-info-circle</v-icon>
                      </v-btn>
                    </template>
                  </v-combobox>
                  <v-btn-toggle v-model="activity.type" mandatory dense color="primary" style="align-self: flex-start;">
                    <v-btn value="strength" small>
                      <v-icon small :class="{'mr-1': true, 'hidden-xs-only': false}">fas fa-dumbbell</v-icon> 
                      <span class="hidden-xs-only">{{ $t('fitness_workouts.strength') || 'Strength' }}</span>
                    </v-btn>
                    <v-btn value="cardio" small>
                      <v-icon small :class="{'mr-1': true, 'hidden-xs-only': false}">fas fa-running</v-icon> 
                      <span class="hidden-xs-only">{{ $t('fitness_workouts.cardio') || 'Cardio' }}</span>
                    </v-btn>
                  </v-btn-toggle>
                </div>
                <div style="display: flex; flex-direction: column; gap: 8px; flex-shrink: 0;">
                  <div style="display: flex; gap: 8px;">
                    <v-btn icon @click="moveActivityUp(index)" :disabled="index === 0" small outlined>
                      <v-icon small>fas fa-arrow-up</v-icon>
                    </v-btn>
                    <v-btn icon @click="moveActivityDown(index)" :disabled="index === editedItem.activities.length - 1" small outlined>
                      <v-icon small>fas fa-arrow-down</v-icon>
                    </v-btn>
                    <v-btn icon color="red" @click="removeActivity(index)" outlined small>
                      <v-icon small>fas fa-trash</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>

              <v-divider class="mb-4"></v-divider>

              <!-- Sets container (Strength) -->
              <div v-if="!activity.type || activity.type === 'strength'">
                <div v-if="Array.isArray(activity.sets)">
                  <v-row v-for="(set, sIndex) in activity.sets" :key="sIndex" align="center" class="mb-2" no-gutters>
                    <v-col cols="2" sm="1" class="text-center font-weight-bold" style="color: grey;">
                      {{ sIndex + 1 }}
                    </v-col>
                    <v-col cols="4" class="px-1">
                      <v-text-field v-model="set.amount" :label="$t('fitness_workouts.amount')" placeholder="0" type="number" outlined dense hide-details></v-text-field>
                    </v-col>
                    <v-col cols="4" class="px-1">
                      <v-text-field v-model="set.weight" :label="$t('fitness_workouts.weight')" placeholder="0" suffix="kg" type="number" outlined dense hide-details></v-text-field>
                    </v-col>
                    <v-col cols="2" class="text-center">
                      <v-btn icon color="grey" @click="removeSet(index, sIndex)">
                        <v-icon small>fas fa-times</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </div>
                <div v-else style="color: orange; margin-bottom: 15px;">
                  <em>Old format detected. Please finish workout to upgrade this activity to the new multi-set format.</em>
                </div>

                <v-btn small text color="primary" @click="addSet(index)" class="mt-2" style="font-weight: bold;">
                  <v-icon left small>fas fa-plus</v-icon>
                  {{ $t("fitness_workouts.add_set") }}
                </v-btn>
              </div>

              <!-- Duration container (Cardio) -->
              <div v-else-if="activity.type === 'cardio'" style="padding: 10px 0;">
                <v-row no-gutters>
                  <v-col cols="4" class="px-1">
                    <v-text-field 
                      v-model="activity.duration" 
                      :label="$t('fitness_workouts.duration_min')" 
                      placeholder="0"
                      type="number" 
                      outlined 
                      dense 
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="4" class="px-1">
                    <v-text-field 
                      v-model="activity.distance" 
                      :label="$t('fitness_workouts.distance')" 
                      placeholder="0"
                      suffix="km"
                      type="number" 
                      outlined 
                      dense 
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="4" class="px-1">
                    <v-text-field 
                      v-model="activity.calories" 
                      :label="$t('fitness_workouts.calories')" 
                      placeholder="0"
                      suffix="kcal"
                      type="number" 
                      outlined 
                      dense 
                      hide-details
                    ></v-text-field>
                  </v-col>
                </v-row>
              </div>
            </v-card>
            
            <div v-if="editedItem.activities.length > 0" class="mt-4 mb-6">
              <v-btn color="secondary" @click="addActivity" elevation="2" large block>
                <v-icon left small>fas fa-plus</v-icon>
                <span style="font-weight: bold;">{{ $t("fitness_workouts.add_activity") }}</span>
              </v-btn>
            </div>
            <div v-if="editedItem.activities.length === 0" class="text-center mt-10 mb-6" style="color: gray; font-size: 1.2em;">
              <v-icon size="64" color="grey lighten-1">fad fa-dumbbell</v-icon><br><br>
              {{ $t("fitness_workouts.no_activities") }}
            </div>
            
            <div v-if="editedItem.id !== -1" class="mb-10 text-center">
              <v-btn text color="error" @click="deleteItem(editedItem)" class="font-weight-bold">
                 <v-icon left small>fas fa-trash</v-icon>
                 {{ $t('general.delete') || 'Delete' }}
              </v-btn>
            </div>
            
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import FitnessWorkouts from "../logic/FitnessWorkouts";
import exrxLinks from "../assets/exrx_links";

export default {
  data: () => ({
    dialog: false,
    menu: false,
    loading: false,
    saving: false,
    isSavingDraft: false,
    autosaveTimer: null,
    pauseWatcher: false,
    liveTimerInterval: null,
    activeTimer: "00:00",
    workouts: [],
    editedIndex: -1,
    search: '',
    filterType: 'all',
    kpiTimeframe: 'month',
    showMoreKPIs: false,
    kpiOrder: [],
    editedItem: {
      id: -1,
      name: "",
      date: new Date().toISOString().substr(0, 10),
      activities: [],
      is_draft: true,
      start_time: null,
      duration: null
    },
    originalItemStr: null,
    defaultItem: {
      id: -1,
      name: "",
      date: new Date().toISOString().substr(0, 10),
      activities: [],
      is_draft: true,
      start_time: null,
      duration: null
    },
    defaultStrengthActivities: [
      "exercises.bench_press", "exercises.incline_bench_press", "exercises.dumbbell_press",
      "exercises.squats", "exercises.deadlift", "exercises.leg_press", "exercises.leg_extension", "exercises.leg_curl", "exercises.calf_raises",
      "exercises.overhead_press", "exercises.lateral_raises", "exercises.shoulder_press_machine", "exercises.rear_delt_fly",
      "exercises.pull_ups", "exercises.lat_pulldown", "exercises.barbell_row", "exercises.seated_cable_row", "exercises.t_bar_rows", "exercises.face_pulls",
      "exercises.push_ups", "exercises.bicep_curls", "exercises.hammer_curls", "exercises.preacher_curls", "exercises.butterfly_machine", "exercises.chest_press_machine", "exercises.cable_crossover",
      "exercises.tricep_extensions", "exercises.tricep_dips", "exercises.skull_crushers",
      "exercises.lunges", "exercises.bulgarian_split_squats", "exercises.hip_thrust", "exercises.smith_machine_squats", "exercises.smith_machine_bench_press",
      "exercises.romanian_deadlift", "exercises.hack_squats", "exercises.front_squats", "exercises.good_mornings",
      "exercises.hip_abduction", "exercises.hip_adduction", "exercises.glute_kickbacks",
      "exercises.calf_raises", "exercises.seated_calf_raises",
      "exercises.barbell_shrugs", "exercises.dumbbell_shrugs", "exercises.kettlebell_swings", "exercises.farmers_walk",
      "exercises.crunches", "exercises.leg_raises", "exercises.ab_crunch_machine", "exercises.side_plank", "exercises.russian_twists"
    ],
    defaultCardioActivities: [
      "exercises.running", "exercises.treadmill", "exercises.cycling", "exercises.indoor_cycling", 
      "exercises.elliptical", "exercises.rowing", "exercises.stairmaster", "exercises.walking", "exercises.plank", "exercises.jump_rope", "exercises.box_jumps"
    ]
  }),

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? this.$t('fitness_workouts.add_workout') : this.$t('general.edit');
    },
    headers() {
      return [
        { text: this.$t("fitness_workouts.date"), value: "date", align: "start" },
        { text: this.$t("fitness_workouts.workout_name"), value: "name" },
        { text: this.$t("fitness_workouts.activities"), value: "activities_count" }
      ];
    },
    availableActivities() {
      let uniqueActivities = new Set([...this.defaultStrengthActivities, ...this.defaultCardioActivities]);
      
      (this.workouts || []).forEach(workout => {
        if (workout.activities && Array.isArray(workout.activities)) {
          workout.activities.forEach(act => {
            if (act && act.name) {
              let nameStr = typeof act.name === 'object' ? (act.name.value || act.name.text || String(act.name)) : String(act.name);
              if (nameStr.trim() !== "") uniqueActivities.add(nameStr.trim());
            }
          });
        }
      });
      return Array.from(uniqueActivities).map(code => ({
        value: code,
        text: String(this.getActivityName(code))
      })).sort((a, b) => a.text.localeCompare(b.text));
    },
    suggestedWorkoutName() {
      let date = this.editedItem.date ? new Date(this.editedItem.date) : new Date();
      let dayIndex = date.getDay();
      const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
      let dayStr = this.$t(`general.day_of_week.${days[dayIndex]}`);
      
      let hour = new Date().getHours();
      if (this.editedItem.start_time) {
         hour = new Date(this.editedItem.start_time).getHours();
      }
      
      let timeStr = "";
      if (hour >= 4 && hour < 12) {
         timeStr = this.$t("general.time_of_day.morning");
      } else if (hour >= 12 && hour < 17) {
         timeStr = this.$t("general.time_of_day.afternoon");
      } else if (hour >= 17 && hour < 21) {
         timeStr = this.$t("general.time_of_day.evening");
      } else {
         timeStr = this.$t("general.time_of_day.night");
      }
      
      return `${dayStr} ${timeStr} ${this.$t("general.time_of_day.workout")}`;
    },
    suggestedDuration() {
      if (!this.editedItem || !this.editedItem.activities) return 0;
      let totalSeconds = 0;
      this.editedItem.activities.forEach(act => {
        if (act.type === 'strength' && Array.isArray(act.sets)) {
          act.sets.forEach(set => {
            totalSeconds += (Number(set.amount) || 0) * 5;
          });
        } else if (act.type === 'cardio') {
          totalSeconds += (Number(act.duration) || 0) * 60;
        }
      });
      return Math.round(totalSeconds / 60) || 0;
    },
    startTimeString: {
      get() {
        if (!this.editedItem || !this.editedItem.start_time) return "";
        let st = Number(this.editedItem.start_time);
        if (!st) return "";
        let d = new Date(st);
        if (isNaN(d.getTime())) return "";
        let hh = String(d.getHours()).padStart(2, '0');
        let mm = String(d.getMinutes()).padStart(2, '0');
        return `${hh}:${mm}`;
      },
      set(val) {
        if (!val || !this.editedItem) return;
        let parts = val.split(":");
        if (parts.length === 2) {
          let st = Number(this.editedItem.start_time);
          let d = st ? new Date(st) : new Date();
          if (isNaN(d.getTime())) d = new Date();
          
          if (this.editedItem.date) {
            let workoutDate = new Date(this.editedItem.date);
            if (!isNaN(workoutDate.getTime())) {
               d.setFullYear(workoutDate.getFullYear());
               d.setMonth(workoutDate.getMonth());
               d.setDate(workoutDate.getDate());
            }
          }
          
          d.setHours(Number(parts[0]));
          d.setMinutes(Number(parts[1]));
          this.$set(this.editedItem, 'start_time', d.getTime());
        }
      }
    },
    filterOptions() {
      let options = [
        { text: this.$t("fitness_workouts.filter_all") || "All", value: 'all' },
        { text: this.$t("fitness_workouts.strength") || "Strength", value: 'strength' },
        { text: this.$t("fitness_workouts.cardio") || "Cardio", value: 'cardio' },
        { divider: true },
        { header: this.$t("fitness_workouts.activities") || "Activities" }
      ];
      return options.concat(this.availableActivities);
    },
    filteredWorkouts() {
      let res = this.workouts;
      if (this.filterType !== 'all') {
        res = res.filter(w => {
          if (!w.activities) return false;
          if (this.filterType === 'strength' || this.filterType === 'cardio') {
             return w.activities.some(act => act.type === this.filterType);
          } else {
             // specific activity filter
             return w.activities.some(act => {
                let actName = act.name && typeof act.name === 'object' ? (act.name.value || act.name.text) : act.name;
                return actName === this.filterType;
             });
          }
        });
      }
      if (this.search) {
         let s = this.search.toLowerCase();
         res = res.filter(w => {
           let matchName = w.name && w.name.toLowerCase().includes(s);
           let matchActivities = false;
           if (w.activities) {
              matchActivities = w.activities.some(act => {
                 let actName = act.name && typeof act.name === 'object' ? (act.name.text || act.name.value) : act.name;
                 if (!actName) return false;
                 let translatedName = String(this.getActivityName(actName)).toLowerCase();
                 return translatedName.includes(s) || String(actName).toLowerCase().includes(s);
              });
           }
           return matchName || matchActivities;
         });
      }
      return res.sort((a, b) => {
         const dateA = new Date(a.date).getTime() || 0;
         const dateB = new Date(b.date).getTime() || 0;
         return dateB - dateA;
      });
    },
    kpiWorkouts() {
       return this.calculateKPI(() => 1);
    },
    kpiDuration() {
       return this.calculateKPI(w => Number(w.duration) || 0);
    },
    kpiActivities() {
       return this.calculateKPI(w => (w.activities ? w.activities.length : 0));
    },
    kpiDistance() {
       return this.calculateKPI(w => {
          if (!w.activities) return 0;
          let dist = 0;
          w.activities.forEach(a => {
             if (a.type === 'cardio' && a.distance) dist += Number(a.distance);
          });
          return dist;
       });
    },
    kpiCalories() {
       return this.calculateKPI(w => {
          if (!w.activities) return 0;
          let cal = 0;
          w.activities.forEach(a => {
             if (a.type === 'cardio' && a.calories) cal += Number(a.calories);
          });
          return cal;
       });
    },
    kpiWeightLifted() {
       return this.calculateKPI(w => {
          if (!w.activities) return 0;
          let weight = 0;
          w.activities.forEach(a => {
             if (a.type === 'strength' && Array.isArray(a.sets)) {
                a.sets.forEach(s => {
                   if (s.amount && s.weight) weight += Number(s.amount) * Number(s.weight);
                });
             }
          });
          return weight;
       });
    },
    kpiSets() {
       return this.calculateKPI(w => {
          if (!w.activities) return 0;
          let sets = 0;
          w.activities.forEach(a => {
             if (a.type === 'strength' && Array.isArray(a.sets)) {
                sets += a.sets.length;
             }
          });
          return sets;
       });
    },
    allKPIs() {
      let vsStr = this.$t('fitness_workouts.vs_last_month') || 'vs last month';
      if (this.kpiTimeframe === 'day') vsStr = this.$t('fitness_workouts.vs_yesterday') || 'vs yesterday';
      else if (this.kpiTimeframe === 'week') vsStr = this.$t('fitness_workouts.vs_last_week') || 'vs last week';

      return [
        {
           title: this.$t('fitness_workouts.kpi_workouts') || 'Workouts',
           value: this.kpiWorkouts.current,
           unit: '',
           percent: this.kpiWorkouts.percent,
           vsText: vsStr
        },
        {
           title: this.$t('fitness_workouts.kpi_duration') || 'Duration',
           value: this.kpiDuration.current,
           unit: 'min',
           percent: this.kpiDuration.percent,
           vsText: vsStr
        },
        {
           title: this.$t('fitness_workouts.kpi_activities') || 'Activities',
           value: this.kpiActivities.current,
           unit: '',
           percent: this.kpiActivities.percent,
           vsText: vsStr
        },
        {
           title: this.$t('fitness_workouts.kpi_distance') || 'Distance',
           value: Math.round(this.kpiDistance.current * 10) / 10,
           unit: 'km',
           percent: this.kpiDistance.percent,
           vsText: vsStr
        },
        {
           title: this.$t('fitness_workouts.kpi_calories') || 'Calories',
           value: Math.round(this.kpiCalories.current),
           unit: 'kcal',
           percent: this.kpiCalories.percent,
           vsText: vsStr
        },
        {
           title: this.$t('fitness_workouts.kpi_weight_lifted') || 'Weight Lifted',
           value: Math.round(this.kpiWeightLifted.current),
           unit: 'kg',
           percent: this.kpiWeightLifted.percent,
           vsText: vsStr
        },
        {
           title: this.$t('fitness_workouts.kpi_sets') || 'Sets',
           value: this.kpiSets.current,
           unit: '',
           percent: this.kpiSets.percent,
           vsText: vsStr
        }
      ];
    },
    orderedKPIs() {
       if (this.kpiOrder.length === 0) return this.allKPIs;
       return this.kpiOrder.map(i => this.allKPIs[i]);
    }
  },

  watch: {
    dialog(val) {
      if (!val) {
        this.close();
      }
    },
    editedItem: {
      handler() {
        if (this.dialog && !this.pauseWatcher && this.editedItem.is_draft) {
          this.triggerAutosave();
        }
      },
      deep: true
    },
    '$route.params.id': {
      handler(newId) {
        if (newId) {
          this.loadWorkoutFromRoute(newId);
        } else {
          if (this.dialog) this.dialog = false;
          this.cleanupDialog();
        }
      },
      immediate: true
    },
    kpiTimeframe(newVal) {
      if (newVal) {
        localStorage.setItem("tracky_kpiTimeframe", newVal);
      }
    }
  },

  mounted() {
    let savedTimeframe = localStorage.getItem("tracky_kpiTimeframe");
    if (savedTimeframe && ['day', 'week', 'month'].includes(savedTimeframe)) {
       this.kpiTimeframe = savedTimeframe;
    }

    let order = [0, 1, 2, 3, 4, 5, 6];
    for (let i = order.length - 1; i > 0; i--) {
       const j = Math.floor(Math.random() * (i + 1));
       [order[i], order[j]] = [order[j], order[i]];
    }
    this.kpiOrder = order;

    this.initialize();
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  },

  beforeDestroy() {
    this.stopLiveTimer();
    if (this.autosaveTimer) clearTimeout(this.autosaveTimer);
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  },

  methods: {
    handleBeforeUnload(e) {
      // Prompt before closing/reloading if actively editing a draft workout
      if (this.dialog && this.editedItem && this.editedItem.is_draft) {
        e.preventDefault();
        e.returnValue = "";
      }
    },

    calculateKPI(getValue) {
      const now = new Date();
      now.setHours(0,0,0,0);
      
      let currentVal = 0;
      let lastVal = 0;

      for (let w of (this.workouts || [])) {
         if (!w.date || w.is_draft) continue;
         let d = new Date(w.date);
         if (isNaN(d.getTime())) continue;
         d.setHours(0,0,0,0);

         if (this.kpiTimeframe === 'month') {
            const currentMonth = now.getMonth();
            const currentYear = now.getFullYear();
            let lastMonth = currentMonth - 1;
            let lastMonthYear = currentYear;
            if (lastMonth < 0) {
              lastMonth = 11;
              lastMonthYear--;
            }
            if (d.getMonth() === currentMonth && d.getFullYear() === currentYear) {
               currentVal += getValue(w);
            } else if (d.getMonth() === lastMonth && d.getFullYear() === lastMonthYear) {
               lastVal += getValue(w);
            }
         } else if (this.kpiTimeframe === 'week') {
            const dayOfWeek = now.getDay() || 7; // 1-7
            const currentWeekStart = new Date(now);
            currentWeekStart.setDate(now.getDate() - dayOfWeek + 1);
            const currentWeekEnd = new Date(currentWeekStart);
            currentWeekEnd.setDate(currentWeekStart.getDate() + 6);
            
            const lastWeekStart = new Date(currentWeekStart);
            lastWeekStart.setDate(currentWeekStart.getDate() - 7);
            const lastWeekEnd = new Date(currentWeekEnd);
            lastWeekEnd.setDate(currentWeekEnd.getDate() - 7);

            if (d >= currentWeekStart && d <= currentWeekEnd) {
               currentVal += getValue(w);
            } else if (d >= lastWeekStart && d <= lastWeekEnd) {
               lastVal += getValue(w);
            }
         } else if (this.kpiTimeframe === 'day') {
            const yesterday = new Date(now);
            yesterday.setDate(now.getDate() - 1);
            if (d.getTime() === now.getTime()) {
               currentVal += getValue(w);
            } else if (d.getTime() === yesterday.getTime()) {
               lastVal += getValue(w);
            }
         }
      }

      let percent = 0;
      if (lastVal > 0) {
        percent = Math.round(((currentVal - lastVal) / lastVal) * 100);
      } else if (currentVal > 0) {
        percent = 100;
      }

      return {
         current: currentVal,
         last: lastVal,
         percent: percent
      };
    },

    async initialize() {
      this.loading = true;
      try {
        var response = await FitnessWorkouts.loadAll();
        if (response.isOK) {
          this.workouts = response.data;
        }
      } catch (error) {
        console.error("Failed to load fitness workouts", error);
      }
      this.loading = false;
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        return d.toLocaleDateString("de-CH", {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      } catch (e) {
        return dateStr;
      }
    },

    async openAddDialog() {
      // Create new empty draft in API instantly
      this.saving = true;
      let newName = this.suggestedWorkoutName;
      let newDate = new Date().toISOString().substr(0, 10);
      let newTime = Date.now();
      
      var res = await FitnessWorkouts.add(
         newName,
         newDate,
         [],
         true, // is_draft
         newTime,
         null
      );
      this.saving = false;
      
      if (res.isOK) {
         let newItem = {
           id: res.data.id,
           name: newName,
           date: newDate,
           activities: [],
           is_draft: true,
           start_time: newTime,
           duration: null
         };
         this.workouts.unshift(newItem);
         this.$router.push('/fitness_workouts/' + res.data.id).catch(()=>{});
      }
    },

    async loadWorkoutFromRoute(id) {
       if (this.workouts.length === 0) {
          await this.initialize();
       }
       let item = this.workouts.find(w => String(w.id) === String(id));
       if (item) {
          this.openDialogWithItem(item);
       }
    },

    startLiveTimer() {
      this.stopLiveTimer();
      if (!this.editedItem.start_time) return;
      
      this.updateTimerDisplay();
      this.liveTimerInterval = setInterval(() => {
        this.updateTimerDisplay();
      }, 1000);
    },

    stopLiveTimer() {
      if (this.liveTimerInterval) {
        clearInterval(this.liveTimerInterval);
        this.liveTimerInterval = null;
      }
    },

    updateTimerDisplay() {
      const diffMs = Date.now() - this.editedItem.start_time;
      const totalSeconds = Math.floor(diffMs / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      this.activeTimer = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    },

    addActivity() {
      this.editedItem.activities.push({
        name: "",
        type: "strength",
        duration: null,
        distance: null,
        calories: null,
        sets: [ { amount: null, weight: null } ]
      });
      
      this.$nextTick(() => {
        setTimeout(() => {
          let dialogs = document.querySelectorAll('.v-dialog--active');
          if (dialogs.length > 0) {
            let d = dialogs[dialogs.length - 1];
            d.scrollTo({ top: d.scrollHeight, behavior: 'smooth' });
          } else {
            window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
          }
        }, 50);
      });
    },

    getActivityName(code) {
      if (!code) return "";
      let strCode = typeof code === 'object' ? (code.value || code.text || String(code)) : String(code);
      if (strCode.startsWith("exercises.")) {
        let translation = this.$t(strCode);
        
        if (this.$i18n.locale !== 'en' && this.$i18n.messages && this.$i18n.messages.en && this.$i18n.messages.en.exercises) {
          let exerciseKey = strCode.split(".")[1];
          let enTranslation = this.$i18n.messages.en.exercises[exerciseKey];
          if (enTranslation && enTranslation !== translation) {
            return `${translation} (${enTranslation})`;
          }
        }
        
        return translation !== strCode ? translation : strCode.replace("exercises.", "").replace(/_/g, " ");
      }
      return strCode;
    },
    getExRxLink(name) {
      if (!name) return null;
      let val = typeof name === 'object' ? (name.value || name.text || String(name)) : String(name);
      return exrxLinks[val] || null;
    },
    getActivityColor(act) {
       let name = act.name && typeof act.name === 'object' ? (act.name.text || act.name.value) : act.name;
       if (!name) return '#1976D2'; // default primary hex
       
       // curated palette of hex colors that look good with white text (dark mode on v-chip)
       const colors = [
          '#E53935', // red darken-1
          '#D81B60', // pink darken-1
          '#8E24AA', // purple darken-1
          '#5E35B1', // deep-purple darken-1
          '#3949AB', // indigo darken-1
          '#1E88E5', // blue darken-1
          '#0288D1', // light-blue darken-2
          '#0097A7', // cyan darken-2
          '#00897B', // teal darken-1
          '#43A047', // green darken-1
          '#558B2F', // light-green darken-3
          '#F57C00', // orange darken-2
          '#F4511E', // deep-orange darken-1
          '#6D4C41', // brown darken-1
          '#546E7A'  // blue-grey darken-1
       ];
       
       let hash = 0;
       for (let i = 0; i < name.length; i++) {
          hash = name.charCodeAt(i) + ((hash << 5) - hash);
       }
       return colors[Math.abs(hash) % colors.length];
    },

    isCardio(name) {
      if (!name) return false;
      let val = typeof name === 'object' ? (name.value || name.text || String(name)) : String(name);
      if (this.defaultCardioActivities.includes(val)) return true;
      
      const cardioTranslations = this.defaultCardioActivities.map(c => String(this.$t(c)).toLowerCase());
      return cardioTranslations.includes(val.trim().toLowerCase());
    },

    autoAssignType(activity) {
      if (!activity || !activity.name) return;
      let val = typeof activity.name === 'object' ? activity.name.value : activity.name;
      activity.name = val; // ensure it is string-bound correctly
      
      if (this.isCardio(val)) {
        activity.type = 'cardio';
      } else {
        if (this.defaultStrengthActivities.includes(val)) {
          activity.type = 'strength';
        } else {
          const strengthTranslations = this.defaultStrengthActivities.map(c => this.$t(c).toLowerCase());
          if (strengthTranslations.includes(val.trim().toLowerCase())) {
            activity.type = 'strength';
          }
        }
      }

      // Auto-prefill from historical workouts
      let prefilled = false;
      for (let w of (this.workouts || [])) {
        if (w.id === this.editedItem.id && this.editedItem.id !== -1) continue; // Skip current workout
        
        if (w.activities && Array.isArray(w.activities)) {
          let pastAct = w.activities.find(a => {
            let aName = typeof a.name === 'object' ? (a.name.value || a.name.text || String(a.name)) : String(a.name);
            return aName === val;
          });
          
          if (pastAct) {
            if (activity.type === 'cardio') {
              if (pastAct.duration || pastAct.distance || pastAct.calories) {
                 activity.duration = Number(pastAct.duration) === 0 ? null : Number(pastAct.duration);
                 activity.distance = Number(pastAct.distance) === 0 ? null : Number(pastAct.distance);
                 activity.calories = Number(pastAct.calories) === 0 ? null : Number(pastAct.calories);
                 prefilled = true;
              }
            } else if (activity.type === 'strength') {
              if (Array.isArray(pastAct.sets) && pastAct.sets.length > 0) {
                let clonedSets = JSON.parse(JSON.stringify(pastAct.sets));
                clonedSets.forEach(s => {
                  if (Number(s.amount) === 0) s.amount = null;
                  if (Number(s.weight) === 0) s.weight = null;
                });
                activity.sets = clonedSets;
                prefilled = true;
              } else if (pastAct.amount || pastAct.weight) {
                activity.sets = [{
                  amount: Number(pastAct.amount) === 0 ? null : Number(pastAct.amount),
                  weight: Number(pastAct.weight) === 0 ? null : Number(pastAct.weight)
                }];
                prefilled = true;
              }
            }
            if (prefilled) break;
          }
        }
      }
      
      // Fallback if not prefilled
      if (!prefilled && activity.type === 'strength' && !Array.isArray(activity.sets)) {
         activity.sets = [{ amount: null, weight: null }];
      }
    },

    removeActivity(index) {
      if (confirm(this.$t("general.delete") + "?")) {
        this.editedItem.activities.splice(index, 1);
      }
    },

    moveActivityUp(index) {
      if (index > 0) {
        let activities = this.editedItem.activities;
        let temp = activities[index];
        this.$set(activities, index, activities[index - 1]);
        this.$set(activities, index - 1, temp);
      }
    },

    moveActivityDown(index) {
      let activities = this.editedItem.activities;
      if (index < activities.length - 1) {
        let temp = activities[index];
        this.$set(activities, index, activities[index + 1]);
        this.$set(activities, index + 1, temp);
      }
    },

    addSet(index) {
      let activity = this.editedItem.activities[index];
      if (Array.isArray(activity.sets)) {
        let lastSet = activity.sets.length > 0 ? activity.sets[activity.sets.length - 1] : { amount: null, weight: null };
        activity.sets.push({ amount: lastSet.amount, weight: lastSet.weight });
      }
    },

    removeSet(activityIndex, setIndex) {
      this.editedItem.activities[activityIndex].sets.splice(setIndex, 1);
    },

    editItem(item) {
      this.$router.push('/fitness_workouts/' + item.id).catch(()=>{});
    },

    openDialogWithItem(item) {
      this.pauseWatcher = true;
      this.editedIndex = this.workouts.findIndex(w => w.id === item.id);
      this.editedItem = Object.assign({ start_time: null, duration: null }, item);
      
      let activitiesCopy = JSON.parse(JSON.stringify(item.activities || []));
      
      activitiesCopy = activitiesCopy.map(act => {
        let type = act.type || "strength";
        if (type === "cardio") {
           return {
             name: act.name,
             type: type,
             duration: Number(act.duration) === 0 ? null : act.duration,
             distance: Number(act.distance) === 0 ? null : act.distance,
             calories: Number(act.calories) === 0 ? null : act.calories
           };
        }
        if (type === "strength" && !Array.isArray(act.sets)) {
           return {
             name: act.name,
             type: type,
             duration: Number(act.duration) === 0 ? null : act.duration,
             sets: [ { amount: Number(act.amount) === 0 ? null : act.amount, weight: Number(act.weight) === 0 ? null : act.weight } ]
           };
        }
        let sets = act.sets || [];
        sets.forEach(s => {
           if (Number(s.amount) === 0) s.amount = null;
           if (Number(s.weight) === 0) s.weight = null;
        });
        return {
           name: act.name,
           type: type,
           duration: Number(act.duration) === 0 ? null : act.duration,
           sets: sets
        };
      });

      this.editedItem.activities = activitiesCopy;
      this.editedItem.duration = Number(item.duration) === 0 ? null : Number(item.duration);
      this.dialog = true;

      if (this.editedItem.is_draft) {
         if (!this.editedItem.start_time) {
            this.editedItem.start_time = Date.now(); // Fallback for old drafts
         }
         this.startLiveTimer();
      }

      this.originalItemStr = JSON.stringify(this.editedItem);

      this.$nextTick(() => {
        this.pauseWatcher = false;
      });
    },

    async deleteItem(item) {
      const index = this.workouts.findIndex(w => w.id === item.id);
      if (confirm(this.$t("general.delete") + "?")) {
        try {
          await FitnessWorkouts.delete(item.id);
          if (index > -1) this.workouts.splice(index, 1);
          
          if (this.dialog && this.editedItem.id === item.id) {
             this.editedItem.is_draft = false; // bypass close confirm
             if (this.$route.params.id) {
               this.$router.push('/fitness_workouts').catch(()=>{});
             } else {
               this.cleanupDialog();
             }
          }
        } catch (error) {
          console.error("Failed to delete fitness workout", error);
        }
      }
    },

    async close() {
      if (this.editedItem && this.editedItem.is_draft) {
        if (!confirm(this.$t("general.close") + "?")) {
          return;
        }
        // If they explicitly close a draft, delete it from the database
        await FitnessWorkouts.delete(this.editedItem.id);
        const index = this.workouts.findIndex(w => w.id === this.editedItem.id);
        if (index > -1) {
          this.workouts.splice(index, 1);
        }
      } else if (this.editedItem) {
        if (this.originalItemStr && this.originalItemStr !== JSON.stringify(this.editedItem)) {
           if (!confirm(this.$t("general.close") + "?")) {
             return;
           }
        }
      }
      
      if (this.$route.params.id) {
         this.$router.push('/fitness_workouts').catch(()=>{});
      } else {
         this.cleanupDialog();
      }
    },

    cleanupDialog() {
      this.dialog = false;
      this.stopLiveTimer();
      if (this.autosaveTimer) {
        clearTimeout(this.autosaveTimer);
      }
      this.pauseWatcher = true;
      this.$nextTick(() => {
        this.editedItem = JSON.parse(JSON.stringify(this.defaultItem));
        this.editedIndex = -1;
        this.pauseWatcher = false;
      });
    },

    triggerAutosave() {
      if (this.autosaveTimer) {
        clearTimeout(this.autosaveTimer);
      }
      this.isSavingDraft = true;
      this.autosaveTimer = setTimeout(() => {
        this.performAutosave();
      }, 2000);
    },

    async performAutosave() {
      try {
        await this._saveApiCall(this.editedItem.is_draft);
      } catch (error) {
        console.error("Failed to autosave fitness workout", error);
      }
      this.isSavingDraft = false;
    },

    async finishWorkout() {
      if (this.autosaveTimer) {
        clearTimeout(this.autosaveTimer);
      }
      this.saving = true;
      this.isSavingDraft = true;
      
      // Calculate final duration in minutes
      if (this.editedItem.start_time) {
        const diffMs = Date.now() - this.editedItem.start_time;
        this.editedItem.duration = Math.round(diffMs / 60000);
      }

      await this._saveApiCall(false); // is_draft = false
      this.saving = false;
      this.isSavingDraft = false;
      
      if (this.$route.params.id) {
         this.$router.push('/fitness_workouts').catch(()=>{});
      } else {
         this.cleanupDialog();
      }
    },

    async saveComplete() {
      // For saving completed workouts manually edited afterwards
      this.saving = true;
      await this._saveApiCall(false);
      this.saving = false;
      
      if (this.$route.params.id) {
         this.$router.push('/fitness_workouts').catch(()=>{});
      } else {
         this.cleanupDialog();
      }
    },

    async _saveApiCall(isDraft) {
      if (!this.editedItem.name || this.editedItem.name.trim() === "") {
        this.editedItem.name = this.suggestedWorkoutName;
      }

      // Ensure strict number types for payload
      const castedActivities = this.editedItem.activities.map(act => {
        let nameStr = typeof act.name === 'object' ? (act.name.value || act.name.text || String(act.name)) : String(act.name);
        
        let typedAct = {
          name: nameStr,
          type: act.type || 'strength'
        };

        if (typedAct.type === 'cardio') {
          typedAct.duration = Number(act.duration) || 0;
          typedAct.distance = Number(act.distance) || 0;
          typedAct.calories = Number(act.calories) || 0;
        } else {
          typedAct.sets = Array.isArray(act.sets) ? act.sets.map(s => ({
            amount: Number(s.amount) || 0,
            weight: Number(s.weight) || 0
          })) : [];
        }
        return typedAct;
      });

      const castedDuration = Number(this.editedItem.duration) || 0;

      try {
        if (this.editedIndex > -1) {
          await FitnessWorkouts.edit(
            this.editedItem.id,
            this.editedItem.name,
            this.editedItem.date,
            castedActivities,
            isDraft,
            this.editedItem.start_time,
            castedDuration
          );
          this.editedItem.is_draft = isDraft;
          Object.assign(this.workouts[this.editedIndex], this.editedItem);
        } else {
          var res = await FitnessWorkouts.add(
            this.editedItem.name,
            this.editedItem.date,
            castedActivities,
            isDraft,
            this.editedItem.start_time,
            castedDuration
          );
          if (res.isOK) {
            this.editedItem.id = res.data.id;
            this.editedItem.is_draft = isDraft;
            this.workouts.unshift(JSON.parse(JSON.stringify(this.editedItem)));
          }
        }
      } catch (error) {
        console.error("Failed to save fitness workout", error);
      }
    }
  },
};
</script>
